<template>
<div>
    <b-navbar class="submenu">
        <b-container>
            <b-navbar-brand href="#">Guide Administration</b-navbar-brand>
            <b-collapse id="nav-collapse-sub" is-nav>
                <b-navbar-nav>
                    <a href="#" @click="loadGuideTab()">Guides</a>
                    <a href="#" @click="createNewGuide()">New</a>
                </b-navbar-nav>
            </b-collapse>
        </b-container>
    </b-navbar>

    <b-alert
        v-model="showAlert"
        :variant="alertVariant"
        dismissible
        class="alert-fixed"
    >
        {{ alertMessage }}
    </b-alert>

    <div id="content">
        <div v-if="isLoading" class="loading-state">
            <b-spinner variant="primary" type="grow"></b-spinner>
            <div class="mt-2">Loading guides...</div>
        </div>

        <b-alert
            v-if="error"
            show
            variant="danger"
            dismissible
        >
            {{ error }}
        </b-alert>

        <div v-if="!isLoading && !error" class="row">
            <!-- Left panel with guide list -->
            <div class="col-md-3 mb-4" v-if="guides">
                <b-form-input placeholder="Search guides" class="mb-2" v-model="search"></b-form-input>
                <small>{{guides.length}} Guides</small>
                <div class="leftSide">
                    <b-list-group>
                        <b-list-group-item 
                            v-for="guide in filteredGuides" 
                            :key="guide.id"
                            :active="selectedGuide && selectedGuide.id === guide.id"
                            @click="selectGuide(guide)"
                            button
                            class="guide-item"
                        >
                            <div class="d-flex align-items-center">
                                <i v-if="guide.icon" :class="guide.icon" class="mr-2"></i>
                                <span>{{guide.title}}</span>
                            </div>
                            <small v-if="guide.tagline" class="text-muted d-block">{{guide.tagline}}</small>
                        </b-list-group-item>
                    </b-list-group>
                </div>
            </div>

            <!-- Right panel with guide details -->
            <div class="col-md-9" v-if="selectedGuide">
                <div class="content-panel">
                    <!-- Guide Header -->
                    <div class="guide-header mb-4">
                        <div class="d-flex justify-content-between align-items-start">
                            <div class="guide-title-container">
                                <div class="d-flex align-items-center">
                                    <i v-if="selectedGuide.icon" :class="selectedGuide.icon" class="mr-2"></i>
                                    <h2 class="mb-0">{{selectedGuide.title}}</h2>
                                </div>
                                <p v-if="selectedGuide.tagline" class="text-muted mb-2">{{selectedGuide.tagline}}</p>
                                <div v-if="selectedGuide.tags && selectedGuide.tags.length" class="tags-container mb-2">
                                    <b-badge 
                                        v-for="(tag, index) in selectedGuide.tags" 
                                        :key="`guide-display-tag-${index}-${tag}`"
                                        variant="secondary"
                                        class="mr-1"
                                    >
                                        {{tag}}
                                    </b-badge>
                                </div>
                                <p class="text-muted mb-0">{{selectedGuide.description}}</p>
                            </div>
                            <div class="action-buttons">
                                <b-button 
                                    variant="outline-secondary" 
                                    size="sm" 
                                    @click="startEditingGuide"
                                    class="edit-button"
                                    title="Edit Guide"
                                >
                                    ✎
                                </b-button>
                            </div>
                        </div>
                    </div>

                    <!-- Sections -->
                    <div class="sections-container">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <h3 class="mb-0">Sections</h3>
                            <b-button 
                                variant="outline-primary" 
                                size="sm"
                                @click="addSection"
                                class="add-button"
                                title="Add Section"
                            >
                                ✚
                            </b-button>
                        </div>
                        
                        <b-list-group>
                            <b-list-group-item 
                                v-for="section in selectedGuide.sections" 
                                :key="section.id"
                                class="section-item"
                            >
                                <div class="section-header" @click="toggleSection(section)">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <div class="d-flex align-items-center">
                                            <i :class="isSectionExpanded(section) ? 'fa fa-chevron-down' : 'fa fa-chevron-right'" class="mr-2 toggle-icon"></i>
                                            <h4 class="mb-0">{{section.title}}</h4>
                                        </div>
                                        <div class="section-actions">
                                            <b-button 
                                                variant="outline-secondary" 
                                                size="sm" 
                                                @click.stop="startEditingSection(section)"
                                                class="edit-button"
                                                title="Edit Section"
                                            >
                                                ✎
                                            </b-button>
                                            <b-button 
                                                variant="outline-danger" 
                                                size="sm" 
                                                @click.stop="openSectionDeleteConfirmation(section)"
                                                class="delete-button"
                                                title="Delete Section"
                                            >
                                                🗑
                                            </b-button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Tasks -->
                                <b-collapse :id="'section-' + section.id" v-model="expandedSections[section.id]">
                                    <div class="tasks-container mt-3">
                                        <div class="d-flex justify-content-between align-items-center mb-2">
                                            <h5 class="mb-0">Tasks</h5>
                                            <b-button 
                                                variant="outline-secondary" 
                                                size="sm"
                                                @click.stop="addTask(section)"
                                                class="add-button"
                                                title="Add Task"
                                            >
                                                ✚
                                            </b-button>
                                        </div>
                                        
                                        <b-list-group>
                                            <b-list-group-item 
                                                v-for="task in section.tasks" 
                                                :key="task.id"
                                                class="task-item"
                                            >
                                                <div class="task-header" @click="toggleTask(section, task)">
                                                    <div class="d-flex justify-content-between align-items-center">
                                                        <div class="d-flex align-items-center">
                                                            <i :class="isTaskExpanded(section, task) ? 'fa fa-chevron-down' : 'fa fa-chevron-right'" class="mr-2 toggle-icon"></i>
                                                            <h5 class="mb-0">{{task.title}}</h5>
                                                        </div>
                                                        <div class="task-buttons">
                                                            <b-button 
                                                                variant="outline-secondary" 
                                                                size="sm" 
                                                                @click.stop="startEditingTask(task)"
                                                                class="edit-button"
                                                                title="Edit Task"
                                                            >
                                                                ✎
                                                            </b-button>
                                                            <b-button 
                                                                variant="outline-danger" 
                                                                size="sm" 
                                                                @click.stop="openDeleteConfirmation(section, task)"
                                                                class="delete-button"
                                                                title="Delete Task"
                                                            >
                                                                🗑
                                                            </b-button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </b-list-group-item>
                                        </b-list-group>
                                    </div>
                                </b-collapse>
                            </b-list-group-item>
                        </b-list-group>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Guide Edit Modal -->
    <b-modal
        v-model="showGuideModal"
        :title="editingGuide && editingGuide.id.startsWith('new-') ? 'New Guide' : 'Edit Guide'"
        size="lg"
        @ok="saveGuide"
        @cancel="cancelEditingGuide"
        ok-title="Save"
        cancel-title="Cancel"
        no-close-on-backdrop
    >
        <b-form v-if="editingGuide">
            <b-form-group label="Title" class="mb-4">
                <b-form-input
                    v-model="editingGuide.title"
                    placeholder="Enter guide title"
                    required
                ></b-form-input>
            </b-form-group>

            <b-form-group label="Tagline" class="mb-4">
                <b-form-input
                    v-model="editingGuide.tagline"
                    placeholder="Enter tagline"
                ></b-form-input>
            </b-form-group>

            <b-form-group label="Tags" class="mb-4">
                <b-form-input
                    v-model="editingGuideTags"
                    placeholder="tag1, tag2, tag3"
                ></b-form-input>
                <div class="mt-2">
                    <b-badge 
                        v-for="(tag, index) in editingGuideTags.split(',').filter(t => t.trim())" 
                        :key="`guide-tag-${index}-${tag.trim()}`"
                        variant="secondary"
                        class="mr-1"
                    >
                        {{tag.trim()}}
                    </b-badge>
                </div>
            </b-form-group>

            <b-form-group label="Description" class="mb-4">
                <b-form-textarea
                    v-model="editingGuide.description"
                    rows="4"
                    placeholder="Enter guide description"
                ></b-form-textarea>
            </b-form-group>

            <b-form-group label="Icon" class="mb-4">
                <div class="d-flex">
                    <b-form-input
                        v-model="editingGuide.icon"
                        placeholder="https://s3.amazonaws.com/your-bucket/your-icon.png"
                        class="flex-grow-1 mr-2"
                    ></b-form-input>
                    <b-button 
                        variant="outline-secondary"
                        @click="openIconPicker('guide')"
                        title="Pick from CDN"
                    >
                        <b-icon-image></b-icon-image>
                    </b-button>
                </div>
                <div class="mt-2 text-center">
                    <img 
                        v-if="editingGuide && editingGuide.icon" 
                        :src="editingGuide.icon" 
                        style="height: 60px; width: auto; max-width: 100%;"
                        @error="$event.target.style.display='none'"
                    />
                    <span v-else class="text-muted">Icon Preview</span>
                </div>
            </b-form-group>
        </b-form>
    </b-modal>

    <!-- Section Edit Modal -->
    <b-modal
        v-model="showSectionModal"
        title="Edit Section"
        size="lg"
        @ok="saveSection"
        @cancel="cancelEditingSection"
        ok-title="Save"
        cancel-title="Cancel"
        no-close-on-backdrop
    >
        <b-form v-if="editingSection">
            <b-form-group label="Title" class="mb-4">
                <b-form-input
                    v-model="editingSection.title"
                    placeholder="Enter section title"
                    required
                ></b-form-input>
            </b-form-group>

            <b-form-group label="Tagline" class="mb-4">
                <b-form-input
                    v-model="editingSection.tagline"
                    placeholder="Enter tagline"
                ></b-form-input>
            </b-form-group>

            <b-form-group label="Tags" class="mb-4">
                <b-form-input
                    v-model="editingSectionTags"
                    placeholder="tag1, tag2, tag3"
                ></b-form-input>
                <div class="mt-2">
                    <b-badge 
                        v-for="(tag, index) in editingSectionTags.split(',').filter(t => t.trim())" 
                        :key="`section-tag-${index}-${tag.trim()}`"
                        variant="secondary"
                        class="mr-1"
                    >
                        {{tag.trim()}}
                    </b-badge>
                </div>
            </b-form-group>

            <b-form-group label="Description" class="mb-4">
                <b-form-textarea
                    v-model="editingSection.description"
                    rows="4"
                    placeholder="Enter section description"
                ></b-form-textarea>
            </b-form-group>

            <b-form-group label="Icon" class="mb-4">
                <div class="d-flex">
                    <b-form-input
                        v-model="editingSection.icon"
                        placeholder="https://s3.amazonaws.com/your-bucket/your-icon.png"
                        class="flex-grow-1 mr-2"
                    ></b-form-input>
                    <b-button 
                        variant="outline-secondary"
                        @click="openIconPicker('section')"
                        title="Pick from CDN"
                    >
                        <b-icon-image></b-icon-image>
                    </b-button>
                </div>
                <div class="mt-2 text-center">
                    <img 
                        v-if="editingSection && editingSection.icon" 
                        :src="editingSection.icon" 
                        style="height: 60px; width: auto; max-width: 100%;"
                        @error="$event.target.style.display='none'"
                    />
                    <span v-else class="text-muted">Icon Preview</span>
                </div>
            </b-form-group>
        </b-form>
    </b-modal>

    <!-- Task Edit Modal -->
    <b-modal
        v-model="showTaskModal"
        title="Edit Task"
        size="lg"
        @ok="saveTask"
        @cancel="cancelEditingTask"
        ok-title="Save"
        cancel-title="Cancel"
        no-close-on-backdrop
    >
        <b-form v-if="editingTask">
            <b-form-group label="Title" class="mb-4">
                <b-form-input
                    v-model="editingTask.title"
                    placeholder="Enter task title"
                    required
                ></b-form-input>
            </b-form-group>

            <b-form-group label="Tagline" class="mb-4">
                <b-form-input
                    v-model="editingTask.tagLine"
                    placeholder="Enter tagline"
                ></b-form-input>
            </b-form-group>

            <b-form-group label="Description" class="mb-4">
                <b-form-textarea
                    v-model="editingTask.description"
                    rows="4"
                    placeholder="Enter task description"
                ></b-form-textarea>
            </b-form-group>

            <b-form-group label="Action Title" class="mb-4">
                <b-form-input
                    v-model="editingTask.actionTitle"
                    placeholder="Enter action button text"
                ></b-form-input>
            </b-form-group>

            <b-form-group label="Action URL" class="mb-4">
                <b-form-input
                    v-model="editingTask.actionUrl"
                    placeholder="Enter action URL"
                ></b-form-input>
            </b-form-group>

            <b-form-group label="Completed Title" class="mb-4">
                <b-form-input
                    v-model="editingTask.completedTitle"
                    placeholder="Enter completed title"
                ></b-form-input>
            </b-form-group>

            <b-form-group label="Completed Description" class="mb-4">
                <b-form-input
                    v-model="editingTask.completedDescription"
                    placeholder="Enter completed description"
                ></b-form-input>
            </b-form-group>

            <b-form-group label="Sub Action URL" class="mb-4">
                <b-form-input
                    v-model="editingTask.subActionUrl"
                    placeholder="Enter sub action URL"
                ></b-form-input>
            </b-form-group>

            <b-form-group label="Sub Action Title" class="mb-4">
                <b-form-input
                    v-model="editingTask.subActionTitle"
                    placeholder="Enter sub action title"
                ></b-form-input>
            </b-form-group>

            <b-form-group label="Icon" class="mb-4">
                <div class="d-flex">
                    <b-form-input
                        v-model="editingTask.icon"
                        placeholder="https://s3.amazonaws.com/your-bucket/your-icon.png"
                        class="flex-grow-1 mr-2"
                    ></b-form-input>
                    <b-button 
                        variant="outline-secondary"
                        @click="openIconPicker('task')"
                        title="Pick from CDN"
                    >
                        <b-icon-image></b-icon-image>
                    </b-button>
                </div>
                <div class="mt-2 text-center">
                    <img 
                        v-if="editingTask && editingTask.icon" 
                        :src="editingTask.icon" 
                        style="height: 60px; width: auto; max-width: 100%;"
                        @error="$event.target.style.display='none'"
                    />
                    <span v-else class="text-muted">Icon Preview</span>
                </div>
            </b-form-group>
        </b-form>
    </b-modal>

    <!-- Icon Picker Modal -->
    <b-modal
        v-model="showIconPickerModal"
        title="Select Icon"
        size="lg"
        @hide="currentIconField = null"
        no-close-on-backdrop
    >
        <div class="icon-picker-header mb-3">
            <div class="d-flex gap-3">
                <b-form-select
                    v-model="selectedIconType"
                    :options="iconTypeOptions"
                    class="filter-select"
                >
                    <template #first>
                        <option value="">All Images</option>
                    </template>
                </b-form-select>
                <b-form-input
                    v-model="iconSearchQuery"
                    placeholder="Search icons..."
                    class="search-input"
                />
            </div>
        </div>
        <div class="icon-grid">
            <div v-for="file in filteredIconFiles" 
                 :key="file.uri" 
                 class="icon-item"
                 @click="selectIcon(file.uri)"
            >
                <img :src="file.uri" 
                     :alt="file.fileName"
                     style="height: 60px; width: auto; max-width: 100%;"
                     @error="$event.target.style.display='none'"
                />
                <div class="icon-name">{{file.fileName}}</div>
            </div>
        </div>
    </b-modal>

    <!-- Delete Confirmation Modal -->
    <b-modal
        v-model="showDeleteConfirmation"
        title="Confirm Delete"
        size="sm"
        @ok="confirmDeleteTask"
        @cancel="cancelDeleteTask"
        ok-title="Delete"
        cancel-title="Cancel"
        ok-variant="danger"
    >
        <p>Are you sure you want to delete this task?</p>
    </b-modal>

    <!-- Section Delete Confirmation Modal -->
    <b-modal
        v-model="showSectionDeleteConfirmation"
        title="Confirm Delete"
        size="sm"
        @ok="confirmDeleteSection"
        @cancel="cancelDeleteSection"
        ok-title="Delete"
        cancel-title="Cancel"
        ok-variant="danger"
    >
        <p>Are you sure you want to delete this section?</p>
    </b-modal>
</div>
</template>

<script>
export default {
    name: 'GuideAdmin',
    data() {
        console.log('Component data initialized');
        return {
            guides: null,
            selectedGuide: null,
            editingGuide: null,
            editingGuideTags: '',
            editingSection: null,
            editingSectionTags: '',
            editingTask: null,
            showGuideModal: false,
            showSectionModal: false,
            showTaskModal: false,
            showIconPickerModal: false,
            cdnFiles: null,
            currentIconField: null,  // Tracks which field we're editing (guide/section/task)
            isLoading: false,
            error: null,
            search: "",
            expandedSections: {},
            expandedTasks: {},
            showAlert: false,
            alertMessage: '',
            alertVariant: 'success',
            selectedIconType: '',
            iconSearchQuery: '',
            iconTypeOptions: [
                { value: '.jpg', text: 'JPG Images' },
                { value: '.jpeg', text: 'JPEG Images' },
                { value: '.png', text: 'PNG Images' },
                { value: '.gif', text: 'GIF Images' },
                { value: '.svg', text: 'SVG Images' }
            ],
            showDeleteConfirmation: false,
            taskToDelete: null,
            showSectionDeleteConfirmation: false,
            sectionToDelete: null,
        }
    },
    computed: {
        filteredGuides() {
            console.log('Computing filtered guides:', this.guides);
            if (!this.guides) return [];
            if (!this.search) return this.guides;
            
            const searchLower = this.search.toLowerCase();
            return this.guides.filter(guide => 
                guide.title.toLowerCase().includes(searchLower) ||
                guide.description.toLowerCase().includes(searchLower)
            );
        },
        filteredIconFiles() {
            if (!this.cdnFiles) return [];
            
            return this.cdnFiles.filter(file => {
                const matchesType = !this.selectedIconType || 
                    file.fileName.toLowerCase().endsWith(this.selectedIconType);
                const matchesSearch = !this.iconSearchQuery || 
                    file.fileName.toLowerCase().includes(this.iconSearchQuery.toLowerCase());
                return matchesType && matchesSearch;
            });
        }
    },
    methods: {
        // Guide methods
        startEditingGuide() {
            if (!this.selectedGuide) return;
            
            // Initialize with default values if they don't exist
            this.editingGuide = {
                id: this.selectedGuide.id,
                title: this.selectedGuide.title || '',
                icon: this.selectedGuide.icon || '',
                tagline: this.selectedGuide.tagline || '',
                tags: this.selectedGuide.tags || '',
                description: this.selectedGuide.description || '',
                sections: this.selectedGuide.sections || []
            };
            
            this.editingGuideTags = this.editingGuide.tags;
            this.showGuideModal = true;
        },
        saveGuide() {
            if (!this.editingGuide) return;

            // Use the tags string directly
            this.editingGuide.tags = this.editingGuideTags;

            const isNewGuide = this.editingGuide.id.startsWith('new-');
            const method = isNewGuide ? 'post' : 'put';
            const url = isNewGuide 
                ? `${this.$config.ApiUri}/Plans`
                : `${this.$config.ApiUri}/Plans/${this.editingGuide.id}`;

            // Make API request to save guide
            this.$http[method](url, this.editingGuide)
                .then(response => {
                    console.log('Guide saved successfully:', response);
                    this.showMessage('Guide saved successfully');

                    // Fetch the updated guide list from the server
                    this.onLoad();
                })
                .catch(err => {
                    console.error('Error saving guide:', err);
                    this.error = 'Failed to save guide. Please try again.';
                });

            // Clear the editingGuide and close the modal
            this.editingGuide = null;
            this.editingGuideTags = '';
            this.showGuideModal = false;
        },
        cancelEditingGuide() {
            this.editingGuide = null;
            this.editingGuideTags = '';
            this.showGuideModal = false;
        },

        // Section methods
        startEditingSection(section) {
            if (!section) return;
            
            // Initialize with default values if they don't exist
            this.editingSection = {
                id: section.id,
                title: section.title || '',
                icon: section.icon || '',
                tagline: section.tagline || '',
                tags: section.tags || '',
                description: section.description || '',
                tasks: section.tasks || []
            };
            
            this.editingSectionTags = this.editingSection.tags;
            this.showSectionModal = true;
        },
        showMessage(message, variant = 'success') {
            console.log('show message...');
            this.alertMessage = message;
            this.alertVariant = variant;
            this.showAlert = true;
            if (variant === 'success') {
                setTimeout(() => {
                this.showAlert = false;
                }, 5000);
            }
        },
        saveSection() {
            if (!this.editingSection || !this.selectedGuide) return;

            // Use the tags string directly
            this.editingSection.tags = this.editingSectionTags;

            // Find the index of the section being edited
            const sectionIndex = this.selectedGuide.sections.findIndex(s => s.id === this.editingSection.id);
            if (sectionIndex !== -1) {
                // Update the section in the selectedGuide with the editingSection values
                this.selectedGuide.sections[sectionIndex] = { ...this.editingSection };
            }
            console.log('saving section...', this.selectedGuide);
            // Make API request to save the guide (which includes the updated section)
            this.$http.put(`${this.$config.ApiUri}/Plans/${this.selectedGuide.id}`, this.selectedGuide)
                .then(response => {
                    console.log('Section saved successfully:', response);
                    this.showMessage('Section saved successfully');

                    // Fetch the updated guide list from the server
                    this.onLoad();
                })
                .catch(err => {
                    console.error('Error saving section:', err);
                    this.error = 'Failed to save section. Please try again.';
                });

            // Clear the editingSection and close the modal
            this.editingSection = null;
            this.editingSectionTags = '';
            this.showSectionModal = false;
        },
        cancelEditingSection() {
            if (this.editingSection) {
                // Check if the section is a new section (empty GUID)
                if (this.editingSection.id === '00000000-0000-0000-0000-000000000000') {
                    // Remove the section from the selectedGuide.sections array
                    this.selectedGuide.sections = this.selectedGuide.sections.filter(
                        section => section.id !== this.editingSection.id
                    );
                }
            }

            this.editingSection = null;
            this.editingSectionTags = '';
            this.showSectionModal = false;
        },
        addSection() {
            if (!this.selectedGuide) return;
            
            // Create new section with default values
            const newSection = {
                id: '00000000-0000-0000-0000-000000000000',
                title: '',
                icon: '',
                tagline: '',
                tags: '',
                description: '',
                tasks: []
            };
            
            if (!this.selectedGuide.sections) {
                this.selectedGuide.sections = [];
            }
            
            this.selectedGuide.sections.push(newSection);
            this.startEditingSection(newSection);
        },

        // Task methods
        startEditingTask(task) {
            if (!task) return;
            
            // Initialize with default values if they don't exist
            this.editingTask = {
                id: task.id,
                icon: task.icon || '',
                title: task.title || '',
                tagLine: task.tagLine || '',
                description: task.description || '',
                actionTitle: task.actionTitle || '',
                actionUrl: task.actionUrl || '',
                completed: task.completed || false,
                completedTitle: task.completedTitle || '',
                completedDescription: task.completedDescription || '',
                subActionTitle: task.subActionTitle || '',
                subActionUrl: task.subActionUrl || ''
            };
            
            this.showTaskModal = true;
        },
        saveTask() {
            if (!this.editingTask || !this.selectedGuide) return;

            // Find the section containing this task
            const sectionIndex = this.selectedGuide.sections.findIndex(s =>
                s.tasks.some(t => t.id === this.editingTask.id)
            );

            if (sectionIndex !== -1) {
                const taskIndex = this.selectedGuide.sections[sectionIndex].tasks.findIndex(
                    t => t.id === this.editingTask.id
                );

                if (taskIndex !== -1) {
                    // Update the task in the selectedGuide
                    this.selectedGuide.sections[sectionIndex].tasks[taskIndex] = {
                        ...this.editingTask
                    };

                    // Make API request to save the guide (which includes the updated task)
                    this.$http.put(`${this.$config.ApiUri}/Plans/${this.selectedGuide.id}`, this.selectedGuide)
                        .then(response => {
                            console.log('Task saved successfully:', response);
                            this.showMessage('Task saved successfully');

                            // Fetch the updated guide list from the server
                            this.onLoad();
                        })
                        .catch(err => {
                            console.error('Error saving task:', err);
                            this.error = 'Failed to save task. Please try again.';
                        });
                }
            }

            // Clear the editingTask and close the modal
            this.editingTask = null;
            this.showTaskModal = false;
        },
        cancelEditingTask() {
            if (this.editingTask) {
                // Check if the task is a new task (empty GUID)
                if (this.editingTask.id === '00000000-0000-0000-0000-000000000000') {
                    // Find the section containing this task
                    const sectionIndex = this.selectedGuide.sections.findIndex(s =>
                        s.tasks.some(t => t.id === this.editingTask.id)
                    );

                    if (sectionIndex !== -1) {
                        // Remove the task from the section
                        this.selectedGuide.sections[sectionIndex].tasks = this.selectedGuide.sections[sectionIndex].tasks.filter(
                            t => t.id !== this.editingTask.id
                        );
                    }
                }
            }

            this.editingTask = null;
            this.showTaskModal = false;
        },
        addTask(section) {
            if (!section) return;

            // Create new task with default values
            const newTask = {
                id: '00000000-0000-0000-0000-000000000000',
                icon: '',
                title: '',
                tagLine: '',
                description: '',
                actionTitle: '',
                actionUrl: '',
                completed: false,
                completedTitle: '',
                completedDescription: '',
                subActionTitle: '',
                subActionUrl: ''
            };

            if (!section.tasks) {
                section.tasks = [];
            }

            section.tasks.push(newTask);
            this.startEditingTask(newTask);
        },
        openDeleteConfirmation(section, task) {
            this.taskToDelete = { section, task };
            this.showDeleteConfirmation = true;
        },
        confirmDeleteTask() {
            if (this.taskToDelete) {
                const { section, task } = this.taskToDelete;
                this.deleteTask(section, task);
            }
            this.taskToDelete = null;
            this.showDeleteConfirmation = false;
        },
        cancelDeleteTask() {
            this.taskToDelete = null;
            this.showDeleteConfirmation = false;
        },
        deleteTask(section, task) {
            const sectionIndex = this.selectedGuide.sections.findIndex(s => s.id === section.id);
            if (sectionIndex !== -1) {
                this.selectedGuide.sections[sectionIndex].tasks = this.selectedGuide.sections[sectionIndex].tasks.filter(t => t.id !== task.id);
                this.$http.put(`${this.$config.ApiUri}/Plans/${this.selectedGuide.id}`, this.selectedGuide)
                    .then(response => {
                        console.log('Task deleted successfully:', response);
                        this.showMessage('Task deleted successfully');
                    })
                    .catch(err => {
                        console.error('Error deleting task:', err);
                        this.error = 'Failed to delete task. Please try again.';
                    });
            }
        },
        openSectionDeleteConfirmation(section) {
            this.sectionToDelete = section;
            this.showSectionDeleteConfirmation = true;
        },
        confirmDeleteSection() {
            if (this.sectionToDelete) {
                this.deleteSection(this.sectionToDelete);
            }
            this.sectionToDelete = null;
            this.showSectionDeleteConfirmation = false;
        },
        cancelDeleteSection() {
            this.sectionToDelete = null;
            this.showSectionDeleteConfirmation = false;
        },
        deleteSection(section) {
            const sectionIndex = this.selectedGuide.sections.findIndex(s => s.id === section.id);
            if (sectionIndex !== -1) {
                this.selectedGuide.sections.splice(sectionIndex, 1);
                this.$http.put(`${this.$config.ApiUri}/Plans/${this.selectedGuide.id}`, this.selectedGuide)
                    .then(response => {
                        console.log('Section deleted successfully:', response);
                        this.showMessage('Section deleted successfully');
                    })
                    .catch(err => {
                        console.error('Error deleting section:', err);
                        this.error = 'Failed to delete section. Please try again.';
                    });
            }
        },

        // Existing methods
        loadGuideTab() {
            console.log('Loading guide tab');
            this.onLoad();
        },
        selectGuide(guide) {
            console.log('Selecting guide:', guide);
            this.selectedGuide = guide;
            this.editingGuide = null;
            this.editingSection = null;
            this.editingTask = null;
            // Reset expanded states when selecting a new guide
            this.expandedSections = {};
            this.expandedTasks = {};
        },
        onLoad() {
            console.log('Starting onLoad');
            this.isLoading = true;
            this.error = null;
            
            console.log('Making API request to:', `${this.$config.ApiUri}/Plans`);
            this.$http.get(`${this.$config.ApiUri}/Plans`)
                .then(response => {
                    console.log('Raw API Response:', response);
                    console.log('Response data:', response.data);
                    
                    // Check if response.data is an array directly
                    if (Array.isArray(response.data)) {
                        this.guides = response.data;
                        console.log('Guides loaded (direct array):', this.guides.length);
                    }
                    // Check if response.data has a data property that's an array
                    else if (response.data && Array.isArray(response.data.data)) {
                        this.guides = response.data.data;
                        console.log('Guides loaded (from data property):', this.guides.length);
                    }
                    // If neither, log the structure and set error
                    else {
                        console.error('Unexpected response structure:', {
                            isArray: Array.isArray(response.data),
                            hasDataProperty: response.data && response.data.data !== undefined,
                            dataType: typeof response.data,
                            keys: response.data ? Object.keys(response.data) : []
                        });
                        this.error = 'Unexpected response format from server';
                    }

                    if (this.guides && this.guides.length > 0 && !this.selectedGuide) {
                        this.selectGuide(this.guides[0]);
                    }
                })
                .catch(err => {
                    console.error('Error loading guides:', err);
                    this.error = 'Failed to load guides. Please try again later.';
                })
                .finally(() => {
                    console.log('Request completed, setting isLoading to false');
                    this.isLoading = false;
                });
        },
        toggleSection(section) {
            this.$set(this.expandedSections, section.id, !this.expandedSections[section.id]);
        },
        isSectionExpanded(section) {
            return this.expandedSections[section.id] || false;
        },
        toggleTask(section, task) {
            this.$set(this.expandedTasks, task.id, !this.expandedTasks[task.id]);
        },
        isTaskExpanded(section, task) {
            return this.expandedTasks[task.id] || false;
        },
        createNewGuide() {
            // Create a new guide with default values
            const newGuide = {
                id: 'new-' + Date.now(),
                title: 'New Guide',
                icon: '',
                tagline: '',
                tags: '',
                description: '',
                sections: []
            };
            
            if (!this.guides) {
                this.guides = [];
            }
            
            this.guides.push(newGuide);
            this.selectGuide(newGuide);
            this.startEditingGuide();
        },
        openIconPicker(field) {
            this.currentIconField = field;
            this.selectedIconType = ''; // Reset filter
            this.iconSearchQuery = ''; // Reset search
            this.loadCdnFiles();
            this.showIconPickerModal = true;
        },
        loadCdnFiles() {
            this.$http.get(`${this.$config.ApiUri}/CMS/Files?ordered=true`)
                .then(response => {
                    this.cdnFiles = response.data.filter(f => 
                        f.fileName.toLowerCase().includes(".jpg") ||
                        f.fileName.toLowerCase().includes(".png") ||
                        f.fileName.toLowerCase().includes(".gif") ||
                        f.fileName.toLowerCase().includes(".svg")
                    );
                })
                .catch(err => {
                    console.error('Error loading CDN files:', err);
                    this.error = 'Failed to load CDN files. Please try again.';
                });
        },
        selectIcon(uri) {
            if (!this.currentIconField) return;
            
            switch(this.currentIconField) {
                case 'guide':
                    this.editingGuide.icon = uri;
                    break;
                case 'section':
                    this.editingSection.icon = uri;
                    break;
                case 'task':
                    this.editingTask.icon = uri;
                    break;
            }
            
            this.showIconPickerModal = false;
            this.currentIconField = null;
        },
    },
    mounted: function() {
        console.log('Component mounted');
        this.onLoad();
    }
}
</script>

<style>
.submenu {
    background-color: gainsboro;
    height: 30px;
    font-size: 14px;
    margin-top: 1px;
}
.submenu a, .submenu a:hover {
    color: #222222;
    text-decoration: none;
    margin-right: 20px;
}
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
}
.leftSide {
    max-width: 100%;
    height: 600px;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 4px;
}
.leftSide .list-group-item {
    cursor: pointer;
    padding: 0.75rem 1rem;
}
.leftSide .list-group-item:hover {
    background-color: #f8f9fa;
}
.leftSide .list-group-item.active,
.leftSide .list-group-item.active:hover,
.leftSide .list-group-item.active:focus {
    background-color: #e9ecef !important;
    border-color: #dee2e6 !important;
    color: #212529 !important;
}
.content-panel {
    padding: 20px;
    background-color: #fff;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}
.guide-header {
    padding-bottom: 20px;
    border-bottom: 1px solid #dee2e6;
}
.sections-container .list-group {
    display: flex;
    flex-direction: column;
    gap: 1px;  /* Add 1px gap between sections */
}
.section-item {
    margin-bottom: 0; /* Remove bottom margin since we're using gap */
    border: 1px solid #dee2e6;
    border-radius: 4px;
}
.section-header {
    cursor: pointer;
    padding: 12px 16px;
    background-color: #e9ecef;  /* Darker background for sections */
    border-bottom: 1px solid #dee2e6;
    box-sizing: border-box;
    height: 90px;
    display: flex;
    align-items: center;
}
.section-header > div, .task-header > div {
    width: 100%;
    margin: 0;
    padding: 0;
}
.section-header:hover, .task-header:hover {
    background-color: #e9ecef;
}
.section-header h4, .task-header h5 {
    margin: 0;
    font-size: 1.1rem;
}
.task-header {
    cursor: pointer;
    padding: 12px 16px;
    background-color: #f8f9fa;  /* Lighter background for tasks */
    border-bottom: 1px solid #dee2e6;
    box-sizing: border-box;
    height: 90px;
    display: flex;
    align-items: center;
}
.task-header > div {
    width: 100%;
    margin: 0;
    padding: 0;
}
.toggle-icon {
    font-size: 0.875rem;
    color: #6c757d;
    width: 16px;
    text-align: center;
}
.task-content {
    flex: 1;
    margin-right: 1rem;
}
.task-title h5 {
    display: flex;
    align-items: center;
}
.task-title h5 i {
    font-size: 1rem;
}
.task-title h5 i.fa-check-circle {
    color: #28a745;
}
.task-actions {
    margin-top: 0.5rem;
    font-size: 0.875rem;
}
.h2-input, .h4-input, .h5-input {
    font-weight: bold;
    font-size: inherit;
    padding: 0;
    border: none;
    background: transparent;
}
.h2-input {
    font-size: 2rem;
}
.h4-input {
    font-size: 1.5rem;
}
.h5-input {
    font-size: 1.25rem;
}
.action-buttons, .section-actions, .task-buttons {
    white-space: nowrap;
    margin-left: 0.5rem;
}
.edit-button, .add-button {
    padding: 0.25rem 0.5rem;
    font-size: 1rem;
    border-width: 1px;
    background-color: transparent;
    min-width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
}
.edit-button:hover, .add-button:hover {
    background-color: #f8f9fa;
    border-color: #6c757d;
}
.edit-button:focus, .add-button:focus {
    box-shadow: none;
}
.icon-input {
    width: 120px;
}
.tagline-input {
    font-style: italic;
    color: #6c757d;
}
.tags-input {
    font-size: 0.875rem;
}
.tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
}
.guide-title-container, .section-title-container {
    flex: 1;
    margin-right: 1rem;
}
.guide-item {
    padding: 0.75rem 1rem;
}
.guide-item small {
    font-size: 0.875rem;
    margin-top: 0.25rem;
}
.icon-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 1rem;
    padding: 1rem;
}
.icon-item {
    cursor: pointer;
    padding: 0.5rem;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    text-align: center;
    transition: all 0.2s;
}
.icon-item:hover {
    background-color: #f8f9fa;
    border-color: #adb5bd;
}
.icon-name {
    font-size: 0.75rem;
    color: #6c757d;
    margin-top: 0.5rem;
    word-break: break-all;
}
.icon-picker-header {
    padding: 0 1rem;
}
.filter-select {
    width: 150px;
}
.gap-3 {
    gap: 1rem;
}
@media (max-width: 768px) {
    .icon-picker-header .d-flex {
        flex-direction: column;
    }
    
    .filter-select,
    .search-input {
        width: 100%;
    }
}
</style>